import { useGetYPickLoginQRCodeQuery } from './apiYPickCredentials';
import React, { useEffect, useRef } from 'react';
import { Title } from '@ydistri/ds';
import LoadingIndicator from '../../../components/global/LoadingIndicator/LoadingIndicator';
import { styled } from 'styled-components';

interface YPickLoginQrCodeProps {
  storeId: number;
}

const QR_CODE_PIXEL_SIZE = 228;

const QrCodePlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${QR_CODE_PIXEL_SIZE}px;
  height: ${QR_CODE_PIXEL_SIZE}px;
`;

/**
 * Displays a QR Code image for YPick login. Placed inside a popup displayed when qr code button is hovered on
 * @param storeId
 * @constructor
 */
const YPickLoginQrCode: React.FC<YPickLoginQrCodeProps> = ({ storeId }) => {
  const { data: qrBlob, isLoading, isError } = useGetYPickLoginQRCodeQuery(storeId);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const image = imageRef.current;
    if (!qrBlob || !image) {
      return undefined;
    }

    const objectUrl = URL.createObjectURL(qrBlob);
    image.src = objectUrl;

    return () => {
      URL.revokeObjectURL(objectUrl);
      if (image.src === objectUrl) {
        image.removeAttribute('src');
      }
    };
  }, [qrBlob]);

  if (isLoading)
    return (
      <QrCodePlaceholder>
        <LoadingIndicator text="Loading QR Code..." />
      </QrCodePlaceholder>
    );
  if (isError)
    return (
      <QrCodePlaceholder>
        <Title>Failed to load QR code</Title>
      </QrCodePlaceholder>
    );

  if (!qrBlob) {
    return null;
  }

  return (
    <img
      ref={imageRef}
      alt={`QR Code for store ${storeId}`}
      width={QR_CODE_PIXEL_SIZE}
      height={QR_CODE_PIXEL_SIZE}
    />
  );
};

export default YPickLoginQrCode;
